import { FC, PropsWithChildren, ReactElement, useMemo } from "react";
import { useRecoilValue } from "recoil";

import {
  FilterMultiSelectBound,
  FilterMultiselectUnbound,
  FiltersActions,
  FilterSearch,
} from "@ryde/controls";
import {
  useDynamicFormsUtils,
  useEmitterListener,
  usePageContext,
} from "@ryde/redux";
import {
  GlobalAppEventEnum,
  tableFiltersSchemaAtomFamily,
  ValueType,
} from "@ryde/shared";

import { PageHeaderFiltersList } from "./page-header-filters-list";
import { useTableFilterSearch } from "./use-table-filter-search";

interface IPageHeaderFilters extends PropsWithChildren {
  isServerSearch?: boolean;
  Actions?: ReactElement;
  /** Hide the free-text search field (e.g. the Dynamic Dashboard reuses filters without search). */
  hideSearch?: boolean;
}

export const PageHeaderFilters: FC<IPageHeaderFilters> = ({
  isServerSearch,
  Actions = <FiltersActions />,
  hideSearch = false,
}) => {
  const { entity } = usePageContext();
  const { getControlDropdownSource, getControlSelectItems } =
    useDynamicFormsUtils();
  const filtersSchema = useRecoilValue(tableFiltersSchemaAtomFamily(entity));
  const boundFiltersIdsWithDropdownSource: [string, ValueType][] =
    useMemo(() => {
      return Object.entries(filtersSchema || {})?.filter(([key]) =>
        getControlDropdownSource(+key),
      );
    }, [filtersSchema, getControlDropdownSource]);

  const selectListFiltersIdsWithItems: [string, ValueType][] = useMemo(() => {
    return Object.entries(filtersSchema || {})?.filter(([key]) =>
      getControlSelectItems(+key),
    );
  }, [filtersSchema, getControlSelectItems]);

  const { searchValue, isError, onChange, onSearchApply } =
    useTableFilterSearch();

  useEmitterListener({
    event: GlobalAppEventEnum.PROCESS_COMPLETED,
    handler: () => onSearchApply?.(),
  });

  return (
    <>
      {!hideSearch && (
        <FilterSearch
          searchValue={searchValue}
          isError={isError}
          onChange={onChange}
          onSearchApply={onSearchApply}
        />
      )}
      <PageHeaderFiltersList />

      {boundFiltersIdsWithDropdownSource?.map(([filterId, val]) => (
        <FilterMultiSelectBound
          customFieldId={+filterId}
          isServerSearch={isServerSearch}
          key={filterId}
          initialCheckedValues={val}
        />
      ))}

      {selectListFiltersIdsWithItems?.map(([filterId, val]) => (
        <FilterMultiselectUnbound
          customFieldId={+filterId}
          isServerSearch={isServerSearch}
          key={filterId}
          initialCheckedValues={val}
        />
      ))}
      {Actions}
    </>
  );
};
